import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, FileText, Headphones, Share2, Brain, Upload, Zap, Eye, Volume2, Clock } from 'lucide-react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import Testimonials from '../components/Coach/Testimonials';
import TabbedHighlights from '../components/Client/TabbedHighlights';
import {
AppStoreButton,
} from "react-mobile-app-button";
import TestimonialsClient from '../components/Client/TestimonialsClient';

const features = [
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Smart File Processing",
    description: "Transform any document into digestible knowledge. PDFs, docs, or web articles - we handle it all."
  },
  {
    icon: <Headphones className="w-6 h-6" />,
    title: "Audio Magic",
    description: "Turn lengthy lectures into concise summaries. Generate audio notes for on-the-go learning."
  },
  {
    icon: <BookOpen className="w-6 h-6" />,
    title: "Interactive Learning",
    description: "Auto-generated quizzes and flashcards that adapt to your understanding."
  },
  {
    icon: <Share2 className="w-6 h-6" />,
    title: "Collaborative Growth",
    description: "Share insights and study materials with your circle. Learn better together."
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: "Personal Learning AI",
    description: "Your AI tutor that evolves with your learning style and pace."
  }
];
const IOSUrl = "https://apps.apple.com/us/app/hyphii-healthy-lifestyle-app/id6737209270";


const testimonials = [
  {
    text: "As a master's student, Edvance has been a game-changer for my thesis work. It's like having a personal research assistant available 24/7! Chatting with research articles and whole folders of content has saved me countless hours of reading and digging through papers.",
    author: "Laurin. S",
    role: "Masters Student",
    avatar: "/laurin.jpg"
  },
  {
    text: "I've been using Edvance for a while now and I must say how much I like it! It's incredibly helpful for summarizing key takeaways from research papers and long videos or podcasts, knowing from the start if the research is what I'm looking for and then allowing me to revisit important points precisely when I need them.",
    author: "Dana S.",
    role: "Economist",
    avatar: "/dana.jpg"
  },
  {
    text: "This is really nice. I don't have any regrets subscribing to Max version. The tool is perfect!!",
    author: "Gracia",
    role: "Researcher",
    avatar: "/gracia.jpg"
  },
  {
    text: "This update 🔥🔥🔥 I have a research lab meeting later today, and we're going to be discussing the implications of AI/tech. I used Edvance to generate a summary and ask a follow-up question & it absolutely delivered. The workflow was much more user-friendly than trying to accomplish the same thing in Bard.",
    author: "Ben D.",
    role: "Post-doc researcher",
    avatar: "/ben.jpg"
  },
  {
    text: "Truly loved interface: it's very straight forward and intuitive vs other ai's. Especially vs having 100 tabs open and copy & pasting back and forth from ChatGPT",
    author: "Natalya Z",
    role: "Pharmaceutical Researcher",
    avatar: "/natalya.jpg"
  },
  {
    text: "I took a 2 hour YouTube video and had the AI in Edvance summarise it. I then asked follow-up questions about the highlighted pieces that got my attention. All told, I was able to collect key takeaways in less than 10 mins. Amazing.",
    author: "DnA",
    role: "Researcher",
    avatar: "/dna.jpg"
  },
  {
    text: "This is the exact tool I've been looking for...I've gathered an overwhelming collection of over 700 bookmarks and 190 open web browser tabs. These resources are my roadmap to research, but navigating through them has become increasingly challenging.",
    author: "Kirsten F.",
    role: "ESG Reporting Analyst",
    avatar: "/kirsten.jpg"
  },
  {
    text: "Edvance is really an excellent tool for understanding my papers. It is just like my personal librarian for the internet!",
    author: "Feng Chun",
    role: "Neuroscience PhD",
    avatar: "/feng.jpg"
  },
  {
    text: "I don't know how you do it but the gap in summary and answer quality to ChatGPT is big. And having real text citations is a game changer.",
    author: "Alex M.",
    role: "Data Scientist",
    avatar: "/alex.jpg"
  }
];

const scrollToHighlights = () => {
  const highlightsSection = document.getElementById('highlights');
  if (highlightsSection) {
    highlightsSection.scrollIntoView({ behavior: 'smooth' });
  }
};

const ClientHome = () => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const featureTimer = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    
    const testimonialTimer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => {
      clearInterval(featureTimer);
      clearInterval(testimonialTimer);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-[#c4ffa0]/10">
      <Header />

      <main className="flex-grow px-4 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center gap-8">
          <div className="w-full sm:w-1/2 order-1 sm:order-2 flex justify-center sm:justify-end overflow-hidden">
            <img
              src="landing_mockup.png"
              alt="Digital Health Twin Visualization"
              className="w-full h-auto max-w-none sm:translate-x-0 scale-100"
            />
          </div>

          <div className="w-full sm:w-1/2 order-2 sm:order-1 text-center sm:text-left mt-[10%]">
            <div className="inline-flex items-center mb-2 bg-[#c4ffa0]/20 px-3 py-1 rounded-full text-xs md:text-sm">
              <span className="mr-1 md:mr-2">✨</span>
              <span className="text-[#2d4a1e] font-medium">YOUR WELLNESS COMPANION</span>
              <span className="ml-1 md:ml-2">✨</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-800">
              Your Complete Wellness Tracker
            </h1>

            <p className="text-lg sm:text-xl text-gray-700 mb-8 leading-relaxed">
              Tired of complicated trackers? Introducing KinOS. The holistic wellness app that lets you easily:
            </p>

            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl">
              <div className="flex items-start gap-3 px-4 sm:px-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-[#c4ffa0]/20 p-1 rounded-full">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-emerald-500"></div>
                  </div>
                </div>
                <p className="flex-grow text-left">Track meals with a photo</p>
              </div>

              <div className="flex items-start gap-3 px-4 sm:px-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-[#c4ffa0]/20 p-1 rounded-full">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-yellow-500"></div>
                  </div>
                </div>
                <p className="flex-grow text-left">Journal moods with your voice/video</p>
              </div>

              <div className="flex items-start gap-3 px-4 sm:px-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-[#c4ffa0]/20 p-1 rounded-full">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-purple-500"></div>
                  </div>
                </div>
                <p className="flex-grow text-left">Log exercise effortlessly</p>
              </div>

              <div className="flex items-start gap-3 px-4 sm:px-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-[#c4ffa0]/20 p-1 rounded-full">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-yellow-500"></div>
                  </div>
                </div>
                <p className="flex-grow text-left">Integrates with apple health</p>
              </div>

              <div className="flex items-start gap-3 px-4 sm:px-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-[#c4ffa0]/20 p-1 rounded-full">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-blue-500"></div>
                  </div>
                </div>
                <p className="flex-grow text-left">See how it all connects with intelligent insights and reminders</p>
              </div>
            </div>

            <div className="flex items-center justify-center sm:justify-start mt-8">
              <AppStoreButton
                url={IOSUrl}
                height={65}
                theme="dark"
                className="hover:opacity-90 transition-opacity duration-300"
              />
            </div>
            <p className="mt-6 text-sm text-gray-500 italic">
              "The difference between who you are and who you could be is separated by the decisions you haven't made yet"
            </p>
          </div>
        </div>
      </main>

      <TestimonialsClient />
      <Footer />

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};
export default ClientHome;